/**
 * Таблица категорий подраздела «Социальная сфера» (US-20) — общая форма
 * блоков «Образование» (FR-3) и «Здравоохранение» (FR-4): категория и
 * одна-две величины к ней, в фиксированном порядке ответа.
 *
 * Правила, общие для обоих блоков:
 *  - **Итоговой строки нет** (FR-3.5, FR-4.4): суммы организаций разных
 *    уровней и объектов разных типов показателями источника не являются.
 *  - **Прочерк источника — не нуль** (FR-6.1): строка без значения
 *    показывает состояние величины, а не «0».
 *  - **Отчётная подпись своя у блока** (FR-5.1): год образования отстаёт
 *    от текущего календарного, а здравоохранение подписывается датой
 *    актуальности «Мониторинга СНП» — это норма, а не рассогласование.
 *  - **Drill-through — только при наличии первичных данных** (FR-7.1);
 *    строка без них неактивна, и ошибка не показывается.
 */
import { Info, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PeriodCaption, SourceNote, StatNoData, StatNumber, fmt } from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  SocialCategoryDetail,
  StatPeriod,
  StatSource,
  StatValue,
} from '../../../types/analytics';

export interface SocialCategoryRowVM {
  key: string;
  name: string;
  /** Величины строки по порядку колонок (без колонки категории). */
  values: Array<StatValue | null>;
  /** Подсказка к наименованию: смысл численности из ответа (FR-3.2). */
  hint?: string | null;
  details: SocialCategoryDetail[];
}

interface Props {
  title: string;
  /** Иконка блока в шапке карточки (макет: знак в зелёном квадрате). */
  icon: LucideIcon;
  /** Заголовки колонок величин; первая колонка — категория. */
  columns: string[];
  columnCategory: string;
  rows: SocialCategoryRowVM[];
  period: StatPeriod | null;
  source: StatSource | null;
  note?: string;
  loading: boolean;
  drillHint: string;
  /** Префикс testid: `<slug>-row-…`, `block-<slug>`. */
  slug: string;
  testId: string;
}

export const SocialCategoryBlock = ({
  title,
  icon: Icon,
  columns,
  columnCategory,
  rows,
  period,
  source,
  note,
  loading,
  drillHint,
  slug,
  testId,
}: Props) => {
  const { t } = useTranslation();
  const [drillRow, setDrillRow] = useState<SocialCategoryRowVM | null>(null);

  return (
    <Card className="h-full shadow-card" data-testid={testId}>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-x-4 gap-y-1 space-y-0">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"
            aria-hidden="true"
          >
            <Icon className="size-4" strokeWidth={1.8} />
          </span>
          <CardTitle className="min-w-0 text-base text-text">{title}</CardTitle>
        </div>
        {/* Подпись блока — своя, из его же данных (FR-5.1). */}
        <PeriodCaption period={period} testId={`${slug}-period`} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : rows.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            <section data-testid={`block-${slug}`}>
              <Table>
                <TableHeader>
                  <TableRow className="border-surface-border hover:bg-transparent">
                    <TableHead className="h-auto py-2 text-text-faint">
                      {columnCategory}
                    </TableHead>
                    {columns.map((column) => (
                      <TableHead
                        key={column}
                        className="h-auto whitespace-nowrap py-2 text-right text-text-faint"
                      >
                        {column}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const drillable = row.details.length > 0;
                    return (
                      <TableRow
                        key={row.key}
                        data-testid={`${slug}-row-${row.key}`}
                        className={cn(
                          'border-surface-border',
                          drillable
                            ? 'cursor-pointer hover:bg-brand-50/60'
                            : 'hover:bg-transparent',
                        )}
                        tabIndex={drillable ? 0 : undefined}
                        role={drillable ? 'button' : undefined}
                        onClick={drillable ? () => setDrillRow(row) : undefined}
                        onKeyDown={
                          drillable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setDrillRow(row);
                                }
                              }
                            : undefined
                        }
                      >
                        <TableCell className="py-3">
                          <span
                            className="block min-w-0 font-semibold leading-snug"
                            title={row.hint ?? undefined}
                          >
                            {row.name}
                          </span>
                        </TableCell>
                        {row.values.map((value, index) => (
                          <TableCell
                            key={columns[index] ?? index}
                            className="py-3 text-right"
                          >
                            <StatNumber
                              value={value}
                              format={fmt}
                              testId={`${slug}-${row.key}-${index}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </section>

            {/* Сноска об источнике первичных данных (макет): строка с
                знаком «i» под таблицей. */}
            {note !== undefined && (
              <p className="mt-3 flex items-start gap-1.5 text-xs leading-snug text-text-faint">
                <Info
                  className="mt-px size-3.5 shrink-0"
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
                <span className="min-w-0">{note}</span>
              </p>
            )}
            <SourceNote source={source} />
          </>
        )}
      </CardContent>

      {/* Первичные данные категории (FR-7.1); целевая глубина перехода
          постановкой не задана — открытый вопрос 9 US-20, поэтому экран
          показывает ровно то, что пришло с блоком. */}
      <Dialog
        open={drillRow !== null}
        onOpenChange={(open) => {
          if (!open) setDrillRow(null);
        }}
      >
        <DialogContent
          className="max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto"
          data-testid={`${slug}-drill`}
        >
          {drillRow !== null && (
            <>
              <DialogHeader>
                <DialogTitle className="text-left text-base leading-snug">
                  {drillRow.name}
                </DialogTitle>
                <p className="text-sm text-text-muted">{drillHint}</p>
              </DialogHeader>
              <ul>
                {drillRow.details.map((detail, index) => (
                  <li
                    key={detail.name ?? index}
                    className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 leading-snug text-text-muted">
                      {detail.name ?? '—'}
                    </span>
                    <StatNumber
                      value={detail.count}
                      format={fmt}
                      withUnit
                      className="shrink-0 text-right"
                    />
                    {detail.people !== null && (
                      <StatNumber
                        value={detail.people}
                        format={fmt}
                        withUnit
                        className="shrink-0 text-right"
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
